import { generateSlug } from '../blog/utils';
import type { ContentPage, ContentPageStatus, CreateContentPagePayload, UpdateContentPagePayload } from './types';

export { generateSlug };

export interface ContentPageFormState {
  title: string;
  slug: string;
  body: string;
}

/**
 * `POST /content-pages` body. `slug` is optional server-side (auto-generated from
 * `title` when omitted) — an empty/untouched slug field is omitted here rather than
 * sent as `''`, so the backend does the generation instead of us sending a value
 * that fails the `^[a-z0-9]+(-[a-z0-9]+)*$` validator.
 */
export function buildCreatePayload(form: ContentPageFormState): CreateContentPagePayload {
  const slug = form.slug.trim();
  return {
    ...(slug ? { slug } : {}),
    title: form.title,
    body: form.body,
  };
}

/**
 * `PATCH /content-pages/:id` body. Slug is immutable on osprey-api's
 * `UpdateContentPageDto` — it is never included here, even if the caller's form
 * state still carries one (the editor renders it read-only, but this is the
 * belt-and-braces guarantee).
 */
export function buildUpdatePayload(form: Pick<ContentPageFormState, 'title' | 'body'>): UpdateContentPagePayload {
  return {
    title: form.title,
    body: form.body,
  };
}

/**
 * The record carries no status field — derive one from the timestamps:
 *  - `publishedAt` unset            → DRAFT (never published)
 *  - `updatedAt` strictly after `publishedAt` → PUBLISHED_EDITED (live, but the
 *    draft has moved on since the last publish)
 *  - otherwise (equal or updatedAt not after) → PUBLISHED
 *
 * The equal-timestamp case (a publish call stamps both `publishedAt` and
 * `updatedAt` to the same instant) falls into the `else` branch, i.e. PUBLISHED —
 * not PUBLISHED_EDITED, since nothing has changed since that publish.
 */
export function derivePageStatus(page: Pick<ContentPage, 'publishedAt' | 'updatedAt'>): ContentPageStatus {
  if (!page.publishedAt) return 'DRAFT';
  const publishedAt = new Date(page.publishedAt).getTime();
  const updatedAt = new Date(page.updatedAt).getTime();
  if (updatedAt > publishedAt) return 'PUBLISHED_EDITED';
  return 'PUBLISHED';
}

export const CONTENT_PAGE_STATUS_LABELS: Record<ContentPageStatus, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  PUBLISHED_EDITED: 'Published · unpublished edits',
};

/** Quick-pick slugs offered on create for the pages merchants ask for most. */
export const SLUG_SUGGESTIONS = ['privacy-policy', 'shipping-policy', 'return-exchange-policy', 'faq', 'terms'] as const;
